import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import './Profile.css';

const Profile = () => {
  const { user, isSuperAdmin, changePassword } = useAuth();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [mfaSaving, setMfaSaving] = useState(false);
  const [mfaMessage, setMfaMessage] = useState('');

  useEffect(() => {
    api.get('/users/mfa')
      .then(res => setMfaEnabled(res.data?.mfaEnabled || false))
      .catch(() => {});
  }, []);

  const handleMfaToggle = async () => {
    setMfaSaving(true);
    setMfaMessage('');
    try {
      const newValue = !mfaEnabled;
      await api.post('/users/mfa', { enabled: newValue });
      setMfaEnabled(newValue);
      setMfaMessage(`MFA ${newValue ? 'enabled' : 'disabled'} successfully.`);
    } catch (err) {
      setMfaMessage(err.response?.data?.error?.message || 'Failed to update MFA setting.');
    } finally {
      setMfaSaving(false);
    }
  };

  if (!user) {
    return (
      <div className="profile-container">
        <div className="profile-card">
          <p>Please log in to view your profile.</p>
        </div>
      </div>
    );
  }

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword !== confirmPassword) {
      setError('New passwords do not match.');
      return;
    }
    if (newPassword.length < 8) {
      setError('New password must be at least 8 characters.');
      return;
    }

    setSaving(true);
    try {
      await changePassword(currentPassword, newPassword);
      setSuccess('Password changed successfully.');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err) {
      setError(err.message || 'Failed to change password.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="profile-container">
      <div className="profile-header">
        <h1>Profile</h1>
        <p className="page-subtitle">Manage your account information</p>
      </div>

      <div className="profile-card">
        <div className="profile-avatar-section">
          <div className="profile-avatar-large">
            {(user.email?.split('@')[0] || 'U')[0].toUpperCase()}
          </div>
        </div>

        <div className="profile-info-section">
          <div className="profile-info-group">
            <label>Email</label>
            <div className="profile-info-value">{user.email}</div>
          </div>

          {user.name && (
            <div className="profile-info-group">
              <label>Name</label>
              <div className="profile-info-value">{user.name}</div>
            </div>
          )}
        </div>
      </div>

      <div className="profile-card profile-card-password">
        <div className="profile-section-header profile-mfa-header">
          <div>
            <h2>Two-Factor Authentication</h2>
            <p className="profile-mfa-desc">Require an email verification code each time you log in.</p>
          </div>
          <div className="profile-mfa-toggle-wrap">
            {mfaMessage && (
              <span className="profile-mfa-msg profile-alert-success">{mfaMessage}</span>
            )}
            {mfaEnabled ? (
              <span className="btn-mfa-toggle btn-mfa-on">Enabled</span>
            ) : (
              <button
                type="button"
                className="btn-mfa-toggle btn-mfa-off"
                onClick={handleMfaToggle}
                disabled={mfaSaving}
              >
                {mfaSaving ? 'Saving...' : 'Enable'}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="profile-card profile-card-password">
        <div className="profile-section-header">
          <h2>Change Password</h2>
        </div>

        <form className="profile-password-form" onSubmit={handleChangePassword}>
          {error && <div className="profile-alert profile-alert-error">{error}</div>}
          {success && <div className="profile-alert profile-alert-success">{success}</div>}

          <div className="profile-info-group">
            <label htmlFor="currentPassword">Current Password</label>
            <input
              type="password"
              id="currentPassword"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              required
              disabled={saving}
            />
          </div>

          <div className="profile-info-group">
            <label htmlFor="newPassword">New Password</label>
            <input
              type="password"
              id="newPassword"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              disabled={saving}
            />
          </div>

          <div className="profile-info-group">
            <label htmlFor="confirmPassword">Confirm New Password</label>
            <input
              type="password"
              id="confirmPassword"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              disabled={saving}
            />
          </div>

          <div className="profile-form-actions">
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Change Password'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Profile;
